/**
 * The longhand-only constraint.
 *
 * Webflow's clipboard format stores declarations in a `styleLess` string, and its parser
 * **silently drops shorthand properties**. A `padding: 2rem` does not error — it just
 * vanishes, and the pasted section renders with no padding.
 *
 * Rather than police that in code review, this module makes shorthand unrepresentable:
 * `LonghandProp` is a closed union, so `{ padding: '2rem' }` is a type error at the point
 * someone writes it.
 *
 * `assertLonghand` is the belt to that braces — it catches anything that reaches the
 * compiler through an `any`, and a test sweeps the entire registry through it.
 */

export type LonghandProp =
  // Box / layout
  | 'display'
  | 'position'
  | 'top'
  | 'right'
  | 'bottom'
  | 'left'
  | 'z-index'
  | 'box-sizing'
  | 'visibility'
  | 'float'
  | 'clear'

  // Spacing — no `margin` / `padding` shorthands
  | 'margin-top'
  | 'margin-right'
  | 'margin-bottom'
  | 'margin-left'
  | 'padding-top'
  | 'padding-right'
  | 'padding-bottom'
  | 'padding-left'

  // Sizing
  | 'width'
  | 'height'
  | 'min-width'
  | 'min-height'
  | 'max-width'
  | 'max-height'
  | 'aspect-ratio'

  // Flexbox — no `flex` / `flex-flow` / `gap` shorthands
  | 'flex-direction'
  | 'flex-wrap'
  | 'justify-content'
  | 'align-items'
  | 'align-content'
  | 'align-self'
  | 'flex-grow'
  | 'flex-shrink'
  | 'flex-basis'
  | 'order'

  // Grid — Webflow writes gaps as `grid-column-gap` / `grid-row-gap`, not `gap`
  | 'grid-template-columns'
  | 'grid-template-rows'
  | 'grid-template-areas'
  | 'grid-auto-columns'
  | 'grid-auto-rows'
  | 'grid-auto-flow'
  | 'grid-column-gap'
  | 'grid-row-gap'
  | 'grid-column-start'
  | 'grid-column-end'
  | 'grid-row-start'
  | 'grid-row-end'
  | 'grid-area'
  | 'justify-items'
  | 'justify-self'
  | 'place-content'

  // Background — no `background` shorthand
  | 'background-color'
  | 'background-image'
  | 'background-position'
  | 'background-size'
  | 'background-repeat'
  | 'background-attachment'
  | 'background-clip'

  // Border — no `border`, `border-width`, `border-color`, `border-style`, `border-radius`
  | 'border-top-width'
  | 'border-top-style'
  | 'border-top-color'
  | 'border-right-width'
  | 'border-right-style'
  | 'border-right-color'
  | 'border-bottom-width'
  | 'border-bottom-style'
  | 'border-bottom-color'
  | 'border-left-width'
  | 'border-left-style'
  | 'border-left-color'
  | 'border-top-left-radius'
  | 'border-top-right-radius'
  | 'border-bottom-left-radius'
  | 'border-bottom-right-radius'

  // Typography — no `font` shorthand, no `text-decoration` shorthand
  | 'font-family'
  | 'font-size'
  | 'font-weight'
  | 'font-style'
  | 'font-variant'
  | 'line-height'
  | 'letter-spacing'
  | 'word-spacing'
  | 'text-align'
  | 'text-transform'
  | 'text-indent'
  | 'text-decoration-line'
  | 'text-decoration-color'
  | 'text-decoration-style'
  | 'text-underline-offset'
  | 'text-shadow'
  | 'white-space'
  | 'word-break'
  | 'overflow-wrap'
  | 'color'

  // Lists — no `list-style` shorthand
  | 'list-style-type'
  | 'list-style-position'
  | 'list-style-image'

  // Overflow — no `overflow` shorthand
  | 'overflow-x'
  | 'overflow-y'

  // Effects — no `transition` shorthand
  | 'opacity'
  | 'box-shadow'
  | 'filter'
  | 'transform'
  | 'transform-origin'
  | 'transition-property'
  | 'transition-duration'
  | 'transition-timing-function'
  | 'transition-delay'

  // Media / misc
  | 'object-fit'
  | 'object-position'
  | 'cursor'
  | 'pointer-events'
  | 'user-select'
  | 'content';

export type Longhand = Partial<Record<LonghandProp, string>>;

/**
 * Properties Webflow silently drops. Checked at runtime by `assertLonghand`.
 *
 * Kept as a separate list rather than derived from `LonghandProp`, because the point is to
 * catch values that bypassed the type system entirely.
 */
export const SHORTHAND_DENYLIST: readonly string[] = [
  'margin',
  'padding',
  'border',
  'border-width',
  'border-color',
  'border-style',
  'border-radius',
  'border-top',
  'border-right',
  'border-bottom',
  'border-left',
  'background',
  'font',
  'flex',
  'flex-flow',
  'gap',
  'row-gap',
  'column-gap',
  'overflow',
  'transition',
  'animation',
  'grid',
  'grid-template',
  'grid-column',
  'grid-row',
  'inset',
  'text-decoration',
  'list-style',
  'place-items',
  'place-self',
  'columns',
  'outline',
];

export class ShorthandError extends Error {
  constructor(
    public readonly prop: string,
    context?: string,
  ) {
    super(
      `Shorthand CSS property "${prop}"${context ? ` in ${context}` : ''} — Webflow silently ` +
        `drops shorthands from styleLess, so the rule would vanish on paste. Use the longhand ` +
        `form (e.g. padding-top/-right/-bottom/-left), or one of the helpers in longhand.ts.`,
    );
    this.name = 'ShorthandError';
  }
}

/** Throws on any shorthand property. Runs in `compileStyleLess` and across the registry in tests. */
export function assertLonghand(decl: Longhand, context?: string): void {
  for (const prop of Object.keys(decl)) {
    if (SHORTHAND_DENYLIST.includes(prop)) throw new ShorthandError(prop, context);
  }
}

/* ------------------------------------------------------------------ *
 * Authoring helpers.
 *
 * These keep writing styles tolerable without reintroducing shorthands — each one expands
 * to the individual longhand properties.
 * ------------------------------------------------------------------ */

/** All four paddings, CSS shorthand order (top, right, bottom, left). */
export const pad = (t: string, r: string, b: string, l: string): Longhand => ({
  'padding-top': t,
  'padding-right': r,
  'padding-bottom': b,
  'padding-left': l,
});

export const padAll = (v: string): Longhand => pad(v, v, v, v);
export const padY = (v: string): Longhand => ({ 'padding-top': v, 'padding-bottom': v });
export const padX = (v: string): Longhand => ({ 'padding-left': v, 'padding-right': v });

export const mar = (t: string, r: string, b: string, l: string): Longhand => ({
  'margin-top': t,
  'margin-right': r,
  'margin-bottom': b,
  'margin-left': l,
});

export const marY = (v: string): Longhand => ({ 'margin-top': v, 'margin-bottom': v });
export const marX = (v: string): Longhand => ({ 'margin-left': v, 'margin-right': v });

/** Centres a block element horizontally. */
export const centreX: Longhand = { 'margin-left': 'auto', 'margin-right': 'auto' };

/** All four corners. */
export const radius = (v: string): Longhand => ({
  'border-top-left-radius': v,
  'border-top-right-radius': v,
  'border-bottom-left-radius': v,
  'border-bottom-right-radius': v,
});

/** All four sides, all three parts — twelve properties. */
export const border = (width: string, style: string, colour: string): Longhand => ({
  'border-top-width': width,
  'border-top-style': style,
  'border-top-color': colour,
  'border-right-width': width,
  'border-right-style': style,
  'border-right-color': colour,
  'border-bottom-width': width,
  'border-bottom-style': style,
  'border-bottom-color': colour,
  'border-left-width': width,
  'border-left-style': style,
  'border-left-color': colour,
});

export const borderTop = (width: string, style: string, colour: string): Longhand => ({
  'border-top-width': width,
  'border-top-style': style,
  'border-top-color': colour,
});

export const borderBottom = (width: string, style: string, colour: string): Longhand => ({
  'border-bottom-width': width,
  'border-bottom-style': style,
  'border-bottom-color': colour,
});

export const transition = (
  property: string,
  duration = '150ms',
  timing = 'ease-out',
): Longhand => ({
  'transition-property': property,
  'transition-duration': duration,
  'transition-timing-function': timing,
});

export const flexRow = (justify = 'flex-start', align = 'center'): Longhand => ({
  display: 'flex',
  'flex-direction': 'row',
  'justify-content': justify,
  'align-items': align,
});

export const flexCol = (justify = 'flex-start', align = 'stretch'): Longhand => ({
  display: 'flex',
  'flex-direction': 'column',
  'justify-content': justify,
  'align-items': align,
});

/** Both grid gaps, which Webflow keys separately. */
export const gridGap = (row: string, column: string = row): Longhand => ({
  'grid-row-gap': row,
  'grid-column-gap': column,
});

/** Serialise declarations into the semicolon-separated string `styleLess` expects. */
export function serialise(decl: Longhand): string {
  return Object.entries(decl)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k}: ${v};`)
    .join(' ');
}
